import * as Sentry from '@sentry/react'
import { useState, useCallback } from 'react'
import {
  type ImageValidationError,
  type UploadApiError,
  uploadImageApi,
  validateImageFile,
  type UploadImageOptions,
} from '../api/uploadImageApi'

export type ImageUploadErrorCode =
  | ImageValidationError
  | UploadApiError
  | 'UPLOAD_ERROR'

const ERROR_CODES = new Set<string>([
  'FILE_TOO_LARGE',
  'INVALID_FILE_TYPE',
  'UPLOAD_NETWORK_ERROR',
  'UPLOAD_HTTP_ERROR',
  'UPLOAD_ERROR',
])

const isErrorCode = (err: unknown): err is ImageUploadErrorCode =>
  typeof err === 'string' && ERROR_CODES.has(err)

interface UseImageUploadResult {
  upload: (file: File, options?: UploadImageOptions) => Promise<string>
  isUploading: boolean
  errorCode: ImageUploadErrorCode | null
  uploadedUrl: string | null
  reset: () => void
}

/**
 * Hook for uploading images to the backend
 * @returns Object with upload function, loading state, error code, and uploaded URL
 */
export function useImageUpload(): UseImageUploadResult {
  const [isUploading, setIsUploading] = useState(false)
  const [errorCode, setErrorCode] = useState<ImageUploadErrorCode | null>(null)
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)

  const upload = useCallback(
    async (file: File, options?: UploadImageOptions): Promise<string> => {
      setIsUploading(true)
      setErrorCode(null)

      try {
        // Validate file
        const validationError = validateImageFile(file)
        if (validationError) {
          setErrorCode(validationError)
          throw new Error(validationError)
        }

        // Upload file
        const response = await uploadImageApi(file, options)
        setUploadedUrl(response.url)
        return response.url
      } catch (err) {
        const code = isErrorCode(err) ? err : 'UPLOAD_ERROR'
        setErrorCode(code)

        if (code !== 'FILE_TOO_LARGE' && code !== 'INVALID_FILE_TYPE') {
          Sentry.captureException(err, {
            tags: { feature: 'image-upload' },
            extra: {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
            },
          })
        }

        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [],
  )

  const reset = useCallback(() => {
    setIsUploading(false)
    setErrorCode(null)
    setUploadedUrl(null)
  }, [])

  return {
    upload,
    isUploading,
    errorCode,
    uploadedUrl,
    reset,
  }
}
